import {
  BrandVariants,
  createDarkTheme,
  createLightTheme,
  Theme,
  themeToTokensObject,
} from '@fluentui/react-components';

const p4Theme: BrandVariants = {
  10: '#030208',
  20: '#151233',
  30: '#1A1C5F',
  40: '#1E2580',
  50: '#323188',
  60: '#443E91',
  70: '#534C9A',
  80: '#625AA2',
  90: '#7168AB',
  100: '#8076B4',
  110: '#8E85BC',
  120: '#9D94C5',
  130: '#ABA4CE',
  140: '#BAB3D6',
  150: '#C9C3DF',
  160: '#D7D3E8',
};

// Misc Theme Notes
// If you see #797775, likely want to use p4NeutralForeground4

type P4ContextMenuTheme = {
  contextMenuHoverBackground1: string;
};

type P4StatusTheme = {
  statusFlagBasic: string;
  statusFlagDanger: string;
  statusFlagSuccess: string;
  statusFlagWarning: string;
  statusFlagInfo: string;
  statusFlagReady: string;
};

const statusFlags: P4StatusTheme = {
  statusFlagBasic: '#BDBDBD',
  statusFlagDanger: '#E3170A',
  statusFlagSuccess: '#4CAF50',
  statusFlagWarning: '#FFA000',
  statusFlagInfo: '#00B9AF',
  statusFlagReady: '#2196f3',
};

const highContrastStatusFlags: P4StatusTheme = {
  statusFlagBasic: '#BDBDBD',
  statusFlagDanger: '#CC6677',
  statusFlagSuccess: '#117733',
  statusFlagWarning: '#DDCC77',
  statusFlagInfo: '#88CCEE',
  statusFlagReady: '#332288',
};

type P4FieldTheme = {
  fieldLabel: string;
  fieldLink: string; //Used on ValueCard for "Link-Type" field text (I.e. ItemInformation Cards).
  fieldLinkForeground: string;
  fieldLinkHoverForeground: string;
};

const fieldTheme: P4FieldTheme = {
  fieldLabel: '#212B36',
  fieldLink: '#534BAE',
  fieldLinkForeground: '#534BAE',
  fieldLinkHoverForeground: '#1A237E',
};

const darkFieldTheme: P4FieldTheme = {
  fieldLabel: '#bdbdbd',
  fieldLink: '#8E86E3',
  fieldLinkForeground: p4Theme['100'],
  fieldLinkHoverForeground: p4Theme['130'],
};

type P4TradeCounterTheme = {
  tradeCounterForeground1: string;
  tradeCounterForeground2: string;
};

const tradeCounterTheme: P4TradeCounterTheme = {
  tradeCounterForeground1: '#1A237E',
  tradeCounterForeground2: '#333333',
};

const darkCounterDarkTheme: P4TradeCounterTheme = {
  tradeCounterForeground1: '#a8abcb',
  tradeCounterForeground2: '#c4c4c4',
};

type P4MenuTheme = {
  colorBrandMenuForeground1: string; //Main menu SVG Shading.
  colorBrandMenuForeground2: string; //Main menu SVG Shading.
  colorBrandMenuForeground3: string; //Main menu SVG Shading.
  colorBrandMenuForeground4: string; //Main menu SVG Shading.
  colorBrandMenuForeground5: string; //Main menu text values.
  colorBrandMenuBackground1: string; //Main menu background.
};

const menuTheme: P4MenuTheme = {
  colorBrandMenuForeground1: '#1A237E',
  colorBrandMenuForeground2: '#808183',
  colorBrandMenuForeground3: '#C9CDCD',
  colorBrandMenuForeground4: '#484644',
  colorBrandMenuForeground5: '#797775',
  colorBrandMenuBackground1: '#FFFFFB',
};

const darkMenuTheme: P4MenuTheme = {
  colorBrandMenuForeground1: '#3949ab',
  colorBrandMenuForeground2: '#808183',
  colorBrandMenuForeground3: '#8D8E8E',
  colorBrandMenuForeground4: '#B5B0AB',
  colorBrandMenuForeground5: '#D6D6D6',
  colorBrandMenuBackground1: '#292929',
};

//MessageBar theme entries - Specifically needed for 'Info' as it uses neutral colours. Warning/Danger can use the built-ins.
type P4MessageTheme = {
  colorInfoBackground: string;
  colorInfoStroke: string;
  colorInfoIcon: string;
};

const messageTheme: P4MessageTheme = {
  colorInfoBackground: '#EBF9FC',
  colorInfoStroke: '#98C6CD',
  colorInfoIcon: '#00B9AF',
};

type P4InputTheme = {
  colorNeutralStrokeAccessible: string;
};

const inputTheme: P4InputTheme = {
  colorNeutralStrokeAccessible: '#CCC',
};

type P4HeaderTheme = {
  colorHeaderBackground: string;
  colorHeaderForeground: string;
  colorHeaderHoverForeground: string;
};

const headerTheme: P4HeaderTheme = {
  colorHeaderBackground: '#FAF9F8',
  colorHeaderForeground: '#484644',
  colorHeaderHoverForeground: '#343434',
};

const darkHeaderTheme: P4HeaderTheme = {
  colorHeaderBackground: '#333333',
  colorHeaderForeground: '#E0E0E0',
  colorHeaderHoverForeground: '#484644',
};

type P4IconTheme = {
  colorFavouriteFilled: string;
};

const iconTheme: P4IconTheme = {
  colorFavouriteFilled: '#EFB034',
};

export const p4LightTheme: Theme &
  P4StatusTheme &
  P4MessageTheme &
  P4MenuTheme &
  P4ContextMenuTheme &
  P4FieldTheme &
  P4IconTheme &
  P4InputTheme &
  P4HeaderTheme &
  P4TradeCounterTheme = {
  ...createLightTheme(p4Theme),
  ...statusFlags,
  ...messageTheme,
  ...menuTheme,
  ...fieldTheme,
  ...inputTheme,
  ...headerTheme,
  ...iconTheme,
  ...tradeCounterTheme,
  spacingHorizontalM: '0', //P4 inputs do not have a large built-in spacer on the left, so removing this from the default of 12px
  // colorCompoundBrandStroke: unknown, //Input underline - Focused
  colorNeutralStrokeAccessible: '#ccc', //Input underline - Passive
  contextMenuHoverBackground1: '#3949ab',
  colorBrandForegroundLink: '#551A8B', // Maintain existing style for light theme - Arguably looks better without this (Just a bit more subtle).
  colorBrandForegroundLinkPressed: '#0000EE', // Maintain existing style for light theme - Arguably looks better without this (Just a bit more subtle).
  colorBrandBackground: '#45439c', // Company selection button.
};

export const p4LightHighContrastTheme: Theme &
  P4StatusTheme &
  P4MessageTheme &
  P4MenuTheme &
  P4ContextMenuTheme &
  P4IconTheme &
  P4HeaderTheme &
  P4TradeCounterTheme = {
  ...createLightTheme(p4Theme),
  ...highContrastStatusFlags,
  ...messageTheme,
  ...menuTheme,
  ...fieldTheme,
  ...headerTheme,
  ...iconTheme,
  ...tradeCounterTheme,
  contextMenuHoverBackground1: '#3949ab',
  colorBrandBackground: '#45439c', // Company selection button.
};

export const p4DarkTheme: Theme & P4StatusTheme & P4MenuTheme & P4HeaderTheme & P4IconTheme & P4TradeCounterTheme = {
  ...createDarkTheme(p4Theme),
  ...statusFlags,
  ...darkMenuTheme,
  ...darkFieldTheme,
  ...darkHeaderTheme,
  ...iconTheme,
  ...darkCounterDarkTheme,
  colorBrandBackground: '#45439c', // Company selection button.
};

export const p4DarkHighContrastTheme: Theme &
  P4StatusTheme &
  P4MenuTheme &
  P4HeaderTheme &
  P4IconTheme &
  P4TradeCounterTheme = {
  ...createDarkTheme(p4Theme),
  ...highContrastStatusFlags,
  ...darkMenuTheme,
  ...darkFieldTheme,
  ...darkHeaderTheme,
  ...iconTheme,
  ...darkCounterDarkTheme,
  colorBrandBackground: '#45439c', // Company selection button.
};

p4DarkTheme.colorBrandForeground1 = p4Theme[110];
p4DarkTheme.colorBrandForeground2 = p4Theme[120];

export const p4Tokens = themeToTokensObject(p4LightTheme);
